import { Injectable } from '@angular/core';
import { logger } from 'src/app/common/services/logging/logging.service';

@Injectable({
	providedIn: 'root'
})
export class WaveformService {
	private audioContext!: AudioContext;
	private analyser!: AnalyserNode;
	private dataArray!: Uint8Array;
	private source!: MediaStreamAudioSourceNode;

	private MIN_DECIBELS = -45;
	private stream !: MediaStream;

	async init() {
		this.audioContext = new AudioContext();

		this.analyser = this.audioContext.createAnalyser();
		this.analyser.minDecibels = this.MIN_DECIBELS;
		this.analyser.fftSize = 256;
	}

	public async startRecording() {
		this.stream = await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
				autoGainControl: true
			}
		});
		this.source = this.audioContext.createMediaStreamSource(this.stream);
		this.source.connect(this.analyser);

		logger.debug(`AudioContext initialized: ${this.audioContext.state}`);

		// If the AudioContext is in a suspended state, resume it
		if (this.audioContext.state === 'suspended') {
			await this.audioContext.resume();
			logger.debug(`AudioContext re-initialized: ${this.audioContext.state}`);
		}

	}

	public async stopRecording() {

		if (this.stream) {
			this.stream.getTracks().forEach(track => track.stop());
			this.stream = null!;
		}
		if (this.source) {
			this.source.disconnect();
			this.source = null!;
		}
		logger.debug(`AudioContext closed: ${this.audioContext.state}`);
	}

	public getAudioData(): number {

		this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
		// @ts-ignore data type is correct
		this.analyser.getByteTimeDomainData(this.dataArray);

		// Calculate RMS level from time domain data
		const rmsLevel = this.calculateRMS(this.dataArray);

		// Calculate the Normalized RMS level
		let result = this.normalizeRMS(rmsLevel);

		return result;
	}

	private calculateRMS = (data: Uint8Array) => {
		let sumSquares = 0;
		for (let i = 0; i < data.length; i++) {
			const normalizedValue = (data[i] - 128) / 128; // Normalize the data
			sumSquares += normalizedValue * normalizedValue;
		}
		return Math.sqrt(sumSquares / data.length);
	};

	private normalizeRMS = (rms: number) => {
		rms = rms * 10;
		const exp = 1.5; // Adjust exponent value; values greater than 1 expand larger numbers more and compress smaller numbers more
		const scaledRMS = Math.pow(rms, exp);

		// Scale between 0.01 (1%) and 1.0 (100%)
		return Math.min(1.0, Math.max(0.01, scaledRMS));
	};

}